import React from "react";
import { motion } from "framer-motion";
import { useLang } from "@/lib/i18n";

/*
  SPRINT 10 — Europe.
  Große Karte. Elegante Animation.
  Constellation-Layout: Berlin ist der Ursprung; Linien zeichnen sich zu europäischen Städten.
*/

// Approximate positions on a 1000x700 stylised Europe canvas
const NODES = [
  { name: "Berlin",     x: 555, y: 300, origin: true },
  { name: "Paris",      x: 400, y: 380 },
  { name: "Wien",       x: 615, y: 400, altName: "Vienna" },
  { name: "Amsterdam",  x: 435, y: 290 },
  { name: "Kopenhagen", x: 560, y: 220, altName: "Copenhagen" },
  { name: "Barcelona",  x: 355, y: 510 },
  { name: "Warschau",   x: 690, y: 300, altName: "Warsaw" },
  { name: "Zürich",     x: 495, y: 405, altName: "Zurich" },
];

const BERLIN = NODES[0];

export default function Europe() {
  const { t, lang } = useLang();
  const titleLines = t.europe.title.split("\n");

  return (
    <section
      id="europe"
      data-testid="section-europe"
      className="relative bg-[#0F1114] py-32 md:py-48 overflow-hidden"
    >
      {/* subtle radial background */}
      <div
        aria-hidden
        className="absolute inset-0 opacity-40 pointer-events-none"
        style={{
          background:
            "radial-gradient(circle at 55% 45%, rgba(245,215,122,0.08) 0%, transparent 60%)",
        }}
      />

      <div className="relative max-w-[1400px] mx-auto px-6 md:px-14">
        {/* Header */}
        <div className="grid lg:grid-cols-12 gap-10 mb-16 md:mb-20">
          <div className="lg:col-span-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
            >
              <div className="flex items-center gap-3 text-[11px] tracking-[0.35em] uppercase text-[#F5D77A]/80 mb-8">
                <span className="h-px w-8 bg-[#F5D77A]" />
                {t.europe.kicker}
              </div>
              <h2
                className="font-display font-light tracking-[-0.035em] leading-[0.98] text-white"
                style={{ fontSize: "clamp(2.75rem, 7vw, 6.5rem)" }}
              >
                {titleLines.map((line, i) => (
                  <span key={i} className="block">
                    {i === 1 ? <span className="text-[#F5D77A]">{line}</span> : line}
                  </span>
                ))}
              </h2>
            </motion.div>
          </div>
          <div className="lg:col-span-4 flex items-end">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.9, delay: 0.2 }}
            >
              <p
                className="font-display font-light tracking-[-0.02em] text-white/85 leading-[1.15]"
                style={{ fontSize: "clamp(1.4rem, 2vw, 1.9rem)" }}
              >
                {t.europe.body}
              </p>
              <p className="mt-6 text-[#F5D77A]/70 text-sm italic tracking-wide">
                {t.europe.note}
              </p>
            </motion.div>
          </div>
        </div>

        {/* SPRINT 10 · GROSSE KARTE */}
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true, margin: "-40px" }}
          transition={{ duration: 1.4 }}
          className="relative border border-white/8 bg-black/30 backdrop-blur-sm"
        >
          <div className="relative w-full aspect-[10/7]">
            <svg
              viewBox="0 0 1000 700"
              className="absolute inset-0 w-full h-full"
              preserveAspectRatio="xMidYMid meet"
            >
              {/* Grid lines — subtle */}
              <defs>
                <pattern id="grid-eu" width="80" height="80" patternUnits="userSpaceOnUse">
                  <path d="M 80 0 L 0 0 0 80" fill="none" stroke="rgba(255,255,255,0.05)" strokeWidth="1" />
                </pattern>
                <radialGradient id="berlin-glow" cx="50%" cy="50%" r="50%">
                  <stop offset="0%" stopColor="#F5D77A" stopOpacity="0.55" />
                  <stop offset="100%" stopColor="#F5D77A" stopOpacity="0" />
                </radialGradient>
              </defs>
              <rect width="1000" height="700" fill="url(#grid-eu)" />

              {/* Stylised Europe silhouette — very abstract */}
              <path
                d="M 200 250 Q 240 180 320 170 Q 400 130 480 150 Q 560 130 640 165 Q 720 180 780 220 Q 820 265 800 340 Q 780 400 720 450 Q 650 500 580 520 Q 500 550 420 545 Q 340 555 280 520 Q 210 480 195 400 Q 180 320 200 250 Z"
                fill="rgba(255,255,255,0.02)"
                stroke="rgba(255,255,255,0.09)"
                strokeWidth="1"
                strokeDasharray="4 6"
              />

              {/* Connection lines — Berlin → all others */}
              {NODES.filter((n) => !n.origin).map((n, i) => (
                <motion.line
                  key={n.name}
                  x1={BERLIN.x}
                  y1={BERLIN.y}
                  x2={n.x}
                  y2={n.y}
                  stroke="#F5D77A"
                  strokeWidth="1"
                  strokeOpacity="0.35"
                  strokeDasharray="4 4"
                  initial={{ pathLength: 0, opacity: 0 }}
                  whileInView={{ pathLength: 1, opacity: 1 }}
                  viewport={{ once: true }}
                  transition={{ duration: 1.4, delay: 0.6 + i * 0.15, ease: [0.22, 1, 0.36, 1] }}
                />
              ))}

              {/* Nodes */}
              {NODES.map((n, i) => {
                const label = lang === "en" && n.altName ? n.altName : n.name;
                return (
                  <motion.g
                    key={n.name}
                    initial={{ opacity: 0, scale: 0.5 }}
                    whileInView={{ opacity: 1, scale: 1 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.6, delay: n.origin ? 0.2 : 0.5 + i * 0.12 }}
                    data-testid={`europe-node-${label.toLowerCase()}`}
                  >
                    {n.origin && <circle cx={n.x} cy={n.y} r="42" fill="url(#berlin-glow)" />}
                    <circle
                      cx={n.x}
                      cy={n.y}
                      r={n.origin ? "7" : "4"}
                      fill={n.origin ? "#F5D77A" : "#ffffff"}
                    />
                    {n.origin && (
                      <motion.circle
                        cx={n.x}
                        cy={n.y}
                        r="12"
                        fill="none"
                        stroke="#F5D77A"
                        strokeOpacity="0.4"
                        strokeWidth="1"
                        initial={{ scale: 0.5, opacity: 1 }}
                        animate={{ scale: 3, opacity: 0 }}
                        transition={{ duration: 3, repeat: Infinity, ease: "easeOut" }}
                      />
                    )}
                    <text
                      x={n.x + (n.origin ? 16 : 10)}
                      y={n.y + 4}
                      fill={n.origin ? "#F5D77A" : "rgba(255,255,255,0.75)"}
                      fontFamily="Manrope, system-ui"
                      fontSize={n.origin ? "16" : "13"}
                      fontWeight={n.origin ? "500" : "300"}
                      letterSpacing={n.origin ? "0.5" : "0.3"}
                    >
                      {label}
                    </text>
                    {n.origin && (
                      <text
                        x={n.x + 16}
                        y={n.y + 22}
                        fill="rgba(245,215,122,0.55)"
                        fontFamily="Manrope, system-ui"
                        fontSize="9"
                        letterSpacing="2"
                      >
                        {lang === "en" ? "ORIGIN" : "URSPRUNG"}
                      </text>
                    )}
                  </motion.g>
                );
              })}
            </svg>
          </div>

          {/* Legend strip */}
          <div className="flex items-center justify-between px-6 md:px-10 py-4 border-t border-white/10 text-[10px] tracking-[0.35em] uppercase text-white/50 font-display">
            <span>{lang === "en" ? "European context" : "Europäischer Kontext"}</span>
            <span className="text-[#F5D77A]/70">{t.europe.cities.length} · Cities</span>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
